import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'

import Alert from '../components/common/Alert'
import Spinner from '../components/common/Spinner'
import Button from '../components/form/Button'
import SubTitle from '../components/title/SubTitle'
import Title from '../components/title/Title'
import { WrapInLayout } from '../layouts'
import {
  createPartscareCheckoutSession,
  createPartscarePortalSession,
  fetchPartscareQuote,
  fetchPartscareSessionStatus,
} from '../api/partscare.api'
import {
  IPartscareCheckoutSession,
  IPartscareQuote,
  IPartscareSessionStatus,
  PARTSCARE_INELIGIBLE_MESSAGE,
} from '../model/IPartscare'

const money = (amount?: number | null, currency?: string | null): string =>
  amount == null
    ? '-'
    : new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(amount)

const miles = (value?: number | null): string =>
  value == null ? '-' : new Intl.NumberFormat('en-US').format(value)

// --
// -- Stripe.js must only be loaded once per publishable key. The key is scope
// -- dependent (it comes back with the quote), so cache per key.
// --
const stripeByKey: Record<string, Promise<Stripe | null>> = {}
const getStripe = (publishableKey: string): Promise<Stripe | null> => {
  if (!stripeByKey[publishableKey]) stripeByKey[publishableKey] = loadStripe(publishableKey)
  return stripeByKey[publishableKey]
}

// --
// -- Stripe's billing portal sends `frame-ancestors 'none'`, so it refuses to
// -- load inside an iframe. If this page is framed, assigning window.location
// -- only moves the frame and the browser blocks it with a CSP error, so send the
// -- top-level window instead. Writing a cross-origin top location is permitted
// -- (reading it is not), and this runs from a click, so the frame has the user
// -- activation that top-level navigation requires.
// --
const openTopLevel = (url: string): void => {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url
      return
    }
  } catch {
    // Sandboxed frame that forbids top navigation: fall through and try locally.
  }
  window.location.href = url
}

const Row: FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div className='flex justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0'>
    <div className='text-sm text-gray-600'>{label}</div>
    <div className={`text-sm text-right ${strong ? 'font-bold' : ''}`}>{value}</div>
  </div>
)

// --
// -- Sends the customer to Stripe's Customer Portal, where they can update their
// -- card, read invoices and cancel. The URL is single-use, so it is minted on
// -- click rather than rendered as a normal link.
// --
const ManageButton: FC<{ onManage: () => void; managing: boolean }> = ({ onManage, managing }) => (
  <div className='mt-3'>
    <Button onClick={onManage} disabled={managing} variant='outline' size='small' isNotFullWidth>
      {managing ? 'Opening...' : 'Manage My PartsCare'}
    </Button>
  </div>
)

const AlreadyCovered: FC<{
  monthlyAmount?: number | null
  currency?: string | null
  onManage: () => void
  managing: boolean
}> = ({ monthlyAmount, currency, onManage, managing }) => (
  <div className='rounded-md bg-green-50 p-4'>
    <div className='flex'>
      <div className='flex-shrink-0'>
        <svg className='h-5 w-5 text-green-400' viewBox='0 0 20 20' fill='currentColor'>
          <path
            fillRule='evenodd'
            d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
            clipRule='evenodd'
          />
        </svg>
      </div>
      <div className='ml-3'>
        <h3 className='text-sm font-medium text-green-800'>You already have PartsCare</h3>
        <p className='mt-1 text-sm text-green-700'>
          This part is already covered by an active PartsCare subscription
          {monthlyAmount != null ? ` at ${money(monthlyAmount, currency)}/month` : ''}.
        </p>
        <ManageButton onManage={onManage} managing={managing} />
      </div>
    </div>
  </div>
)

const PartscarePage: FC = () => {
  const [searchParams] = useSearchParams()

  const orderId = searchParams.get('order_id') || ''
  const productId = searchParams.get('product_id') || ''
  // HMAC proving this link came from us. The API rejects the request without it.
  const sig = searchParams.get('sig') || ''
  // Present only after Stripe redirects the customer back to this page.
  const returnedSessionId = searchParams.get('session_id') || ''

  const [quote, setQuote] = useState<IPartscareQuote | null>(null)
  const [checkout, setCheckout] = useState<IPartscareCheckoutSession | null>(null)
  const [sessionStatus, setSessionStatus] = useState<IPartscareSessionStatus | null>(null)

  const [loading, setLoading] = useState<boolean>(true)
  const [starting, setStarting] = useState<boolean>(false)
  const [managing, setManaging] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const paramsMissing = !orderId || !productId || !sig

  // --
  // -- Coming back from Stripe: confirm the subscription before anything else.
  // -- The return URL is built server side and carries the same order_id,
  // -- product_id and sig as the original offer link.
  // --
  useEffect(() => {
    if (!returnedSessionId) return
    let cancelled = false

      ; (async () => {
        try {
          const status = await fetchPartscareSessionStatus(orderId, returnedSessionId, sig)
          if (!cancelled) setSessionStatus(status)
        } catch (e) {
          if (!cancelled) setError((e as Error).message)
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()

    return () => {
      cancelled = true
    }
  }, [returnedSessionId, orderId, sig])

  // --
  // -- Normal entry: quote PartsCare for this order item.
  // --
  useEffect(() => {
    if (returnedSessionId) return
    if (paramsMissing) {
      setLoading(false)
      return
    }
    let cancelled = false

      ; (async () => {
        setLoading(true)
        try {
          const result = await fetchPartscareQuote(orderId, productId, sig)
          if (!cancelled) setQuote(result)
        } catch (e) {
          if (!cancelled) setError((e as Error).message)
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()

    return () => {
      cancelled = true
    }
  }, [orderId, productId, sig, returnedSessionId, paramsMissing])

  const handleStartCheckout = useCallback(async () => {
    if (!quote) return
    setStarting(true)
    setError('')
    try {
      const session = await createPartscareCheckoutSession(quote.orderId, quote.orderItemId, sig)
      setCheckout(session)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setStarting(false)
    }
  }, [quote, sig])

  const handleManage = useCallback(async () => {
    const orderItemId = quote?.orderItemId ?? Number(productId)
    if (!orderId || !orderItemId) return

    setManaging(true)
    setError('')
    try {
      const { url: portalUrl } = await createPartscarePortalSession(orderId, orderItemId, sig)
      // Single-use URL: hand the browser straight over to Stripe.
      openTopLevel(portalUrl)
    } catch (e) {
      setError((e as Error).message)
      setManaging(false)
    }
  }, [orderId, productId, quote, sig])

  const stripePromise = useMemo(
    () => (checkout?.publishableKey ? getStripe(checkout.publishableKey) : null),
    [checkout?.publishableKey],
  )

  const embeddedOptions = useMemo(
    () => (checkout?.clientSecret ? { clientSecret: checkout.clientSecret } : { clientSecret: null }),
    [checkout?.clientSecret],
  )

  const content = () => {
    if (loading) return <Spinner label={returnedSessionId ? 'Confirming your payment' : 'Loading'} />

    if (paramsMissing)
      return <Alert>This PartsCare link is incomplete. Please use the link from your order email.</Alert>

    // -- Returned from Stripe --------------------------------------------------
    if (returnedSessionId) {
      if (error) return <Alert>{error}</Alert>

      if (sessionStatus?.partscareActive)
        return (
          <div className='flex flex-col gap-4'>
            <div className='rounded-md bg-green-50 p-4'>
              <h3 className='text-sm font-medium text-green-800'>Your PartsCare cover is active</h3>
              <p className='mt-1 text-sm text-green-700'>
                You will be billed{' '}
                {money(sessionStatus.monthlyAmount, sessionStatus.currency)} per month
                {sessionStatus.coverageYears
                  ? `, with coverage up to ${sessionStatus.coverageYears} years`
                  : ''}
                .
                {sessionStatus.customerEmail
                  ? ` A confirmation was sent to ${sessionStatus.customerEmail}.`
                  : ''}
              </p>
              <ManageButton onManage={handleManage} managing={managing} />
            </div>
            <p className='text-xs text-gray-500'>
              Update your card, view invoices or cancel at any time from the management page.
            </p>
          </div>
        )

      if (sessionStatus?.status === 'open')
        return (
          <div className='flex flex-col gap-4'>
            <Alert>Your payment was not completed, so PartsCare was not started.</Alert>
            <Button onClick={() => window.location.reload()} isNotFullWidth>
              Try again
            </Button>
          </div>
        )

      return <Alert>We could not confirm your PartsCare payment. Please contact customer service.</Alert>
    }

    // -- Quote / purchase -----------------------------------------------------
    if (error) return <Alert>{error}</Alert>
    if (!quote) return <Alert>We could not load this PartsCare offer.</Alert>

    if (quote.alreadyHasPartscare)
      return (
        <AlreadyCovered
          monthlyAmount={quote.monthlyAmount}
          currency={quote.currency}
          onManage={handleManage}
          managing={managing}
        />
      )

    // The offer window has closed. Checked before eligibility because the part is
    // still eligible — it is enrolment that is no longer open, and the server
    // refuses checkout, so showing a price and a dead button would be worse.
    if (quote.offerExpired)
      return (
        <div className='flex flex-col gap-4'>
          <div className='rounded-md border border-gray-200 p-4'>
            <SubTitle>{quote.partName || 'Part'}</SubTitle>
            {quote.vehicle ? <div className='text-sm text-gray-600'>{quote.vehicle}</div> : null}
          </div>
          <Alert>
            The PartsCare offer for this purchase has expired. PartsCare can only be added
            within a year of buying the part.
          </Alert>
        </div>
      )

    if (!quote.eligible)
      return (
        <div className='flex flex-col gap-4'>
          <div className='rounded-md border border-gray-200 p-4'>
            <SubTitle>{quote.partName || 'Part'}</SubTitle>
            {quote.vehicle ? <div className='text-sm text-gray-600'>{quote.vehicle}</div> : null}
          </div>
          <Alert>
            {PARTSCARE_INELIGIBLE_MESSAGE[
              quote.ineligibleReason as keyof typeof PARTSCARE_INELIGIBLE_MESSAGE
            ] || 'This part is not eligible for PartsCare.'}
          </Alert>
        </div>
      )

    return (
      <div className='flex flex-col gap-6'>
        <div className='rounded-md border border-gray-200 p-4 flex flex-col gap-1'>
          <SubTitle>{quote.partName || 'Part'}</SubTitle>
          {quote.vehicle ? <div className='text-sm text-gray-600 mb-2'>{quote.vehicle}</div> : null}

          <Row label='Part selling price' value={money(quote.partPrice, quote.currency)} />
          <Row label='Vehicle mileage' value={`${miles(quote.mileage)} mi`} />
          <Row
            label='Coverage'
            value={quote.coverageYears ? `Up to ${quote.coverageYears} years` : '-'}
          />
          <Row label='Monthly rate' value={quote.ratePercent ? `${quote.ratePercent}%/mo` : '-'} />
          <Row
            label='Your price'
            value={`${money(quote.monthlyAmount, quote.currency)}/month`}
            strong
          />
        </div>

        {quote.clampedToMinimum ? (
          <p className='text-xs text-gray-500'>
            This part prices below our {money(10, quote.currency)}/month minimum, so the minimum
            applies.
          </p>
        ) : null}
        {quote.clampedToMaximum ? (
          <p className='text-xs text-gray-500'>
            This part prices above our {money(59.99, quote.currency)}/month maximum, so the maximum
            applies.
          </p>
        ) : null}

        {checkout && stripePromise ? (
          <div>
            <EmbeddedCheckoutProvider stripe={stripePromise} options={embeddedOptions}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        ) : (
          <div className='flex flex-col gap-3'>
            <Button onClick={handleStartCheckout} disabled={starting} size='large'>
              {starting
                ? 'Starting checkout...'
                : `Subscribe for ${money(quote.monthlyAmount, quote.currency)}/month`}
            </Button>
            <p className='text-xs text-gray-500'>
              Billed monthly. Cancel any time. Eligibility may vary by part type, mileage,
              condition, and selling price.
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <WrapInLayout title='PartsCare' layout='col1'>
      <div className='w-full max-w-[720px] mx-auto flex flex-col gap-4'>
        <Title>PartsCare Monthly Protection</Title>
        {content()}
      </div>
    </WrapInLayout>
  )
}

export default PartscarePage
